/**
 * Flight → pickup-time rules for the client booking flow.
 *
 * The flight's landing time is the driver's default arrival; the client can push it later with a
 * "delay driver arrival" offset. Backend flight times are ISO strings carrying the AIRPORT-local
 * offset — display formatting must honour that offset, not the browser timezone, or a Sydney
 * arrival viewed from Perth shows the wrong wall-clock time. Instant math (delay, validation) is
 * offset-agnostic.
 */
import { format as formatDate } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { FlightDetails, FlightLeg, FlightStatus } from "@/redux/apis/clientFlight/types";

/**
 * The best-known landing instant, or null when the flight is unresolved (not found, or the lookup
 * returned no times).
 *
 * Prefers the GATE family (parked, doors open) over the RUNWAY family (wheels down) — the passenger
 * cannot leave the aircraft at touchdown, and the taxi can run 25 minutes. Within a family it reads
 * actual, then the live estimate, then the published schedule; the whole gate family outranks the
 * whole runway one, so a gate schedule is returned ahead of a runway actual.
 *
 * Runway is the floor rather than null because the gate fields are only populated from a tracked
 * flight row: on the create path there is none, and a null landing reads as "not flight-derived".
 */
export const landingTime = (flight?: FlightDetails | null): string | null => resolveLanding(flight)?.iso ?? null;

/**
 * The chain, in priority order — the single source for both the landing value and its label, so the
 * time on screen and the word describing it can never disagree.
 */
const LANDING_FIELDS = ["gateActual", "gateEstimated", "gateScheduled", "actual", "estimated", "scheduled"] as const;

export type LandingSource = (typeof LANDING_FIELDS)[number];

/**
 * The chain applied to ONE leg. The single place the order is walked, so every reader of a leg time —
 * the landing the pickup is built on, and the leg row the card prints — is walking the same list.
 */
const pickLegTime = (leg?: FlightLeg | null): { iso: string; source: LandingSource } | null => {
  if (!leg) return null;
  for (const source of LANDING_FIELDS) {
    const iso = leg[source];
    if (iso) return { iso, source };
  }
  return null;
};

/** `landingTime` with its provenance — which field the time came from, for labelling it. */
export const resolveLanding = (flight?: FlightDetails | null): { iso: string; source: LandingSource } | null =>
  pickLegTime(flight?.arrival);

/**
 * The time a LEG puts on screen — the same chain, exported so a display surface cannot run its own.
 *
 * A private runway chain on a display is not a duplicate that happens to agree; it is a slot showing a
 * touchdown estimate where the reader expects the gate time the pickup is derived from, with no label
 * saying which of the two they have.
 *
 * Safe on the DEPARTURE leg, where no gate time exists anywhere in the system: `FlightDeparture` carries
 * no gate columns (only `FlightArrival` does) and `Flight::$departureGate` holds a gate NUMBER like "B12",
 * so those three rungs are always null there and the chain falls through to the runway tail. One helper
 * serves both ends of a card without either learning the other's shape.
 */
export const legTime = (leg?: FlightLeg | null): string | null => pickLegTime(leg)?.iso ?? null;

/**
 * What to call the arrival we resolved. Keyed on the FIELD, never the flight's status: a `landed`
 * status does not imply a gate time exists, and a `scheduled` status beside a gate schedule is still
 * only a schedule.
 *
 * Three labels, per the UX review's taxonomy — one label for every value is the same bug moved from
 * "the numbers disagree" to "the word doesn't match the thing":
 *
 *   runway / wheels-down            → Landed
 *   gate / doors-open               → At gate   (what the pickup math runs on)
 *   live runway ETA                 → Estimated landing
 *   schedule only, no live tracking → Scheduled landing
 *
 * Every label names a LANDING, so only the driver row on the panel says "arrival" — the reader never has
 * to work out which of two "arrival"s they are looking at from position alone. That is what the
 * vocabulary split was for; it never required flattening these four into one word, which would throw
 * away the only thing on the card that says how far to TRUST the number. "At gate 6:10" is a fact about
 * where the aircraft is. "Scheduled landing 6:10" is a timetable entry that may move all day.
 *
 * A gate ESTIMATE is still "At gate": the review's own worked example labels the 11:57 gate estimate
 * the engine used that way. "At gate" is vocabulary dispatchers already know from tracking apps, and
 * unlike "Arrives" it cannot mean either event. Never imply gate-level precision from a schedule.
 *
 * The runway estimate is the fourth string (Kevin, 26 Aug — the taxonomy is four, not three). A live
 * ETA is neither a touchdown nor a published plan, and naming it "Scheduled arrival" would be the same
 * class of lie the taxonomy exists to prevent. Reachable only when no gate value and no runway actual
 * exist.
 *
 * Note for the operator surface: its label comes from the backend's STORED source via
 * `appliedArrivalLabel`, not from this function — that panel describes the arrival the engine used,
 * which a live lookup cannot tell it.
 *
 * @deprecated No caller since the client flight card collapsed to one neutral "Lands" line (the pickup is
 * derived from whichever arrival is shown, so how far to trust it is no longer a question the reader acts
 * on). Kept for the rider app and any surface that still has two arrivals to tell apart; the operator row
 * is `appliedArrivalLabel` and is unaffected. Delete once nothing is waiting on it.
 */
export const arrivalLabel = (source: LandingSource): string => {
  switch (source) {
    case "gateActual":
    case "gateEstimated":
      return "At gate";
    case "actual":
      return "Landed";
    case "estimated":
      return "Estimated landing";
    case "gateScheduled":
    case "scheduled":
      return "Scheduled landing";
  }
};

/**
 * Re-express a UTC instant as an ISO string carrying the ARRIVAL AIRPORT's offset, so it can be handled
 * by the same helpers as every other flight time here.
 *
 * The stored derived arrival is DATE_ATOM UTC while the flight lookup's times carry the airport's own
 * offset. `asAirportWallClock` reads the offset out of the string, so a `+00:00` value yields offset 0
 * and renders the UTC wall clock — "At gate 01:57" for a Sydney arrival, ten hours early. Converting
 * once here means no caller has to know which of the two conventions it is holding.
 */
export const utcIsoToAirportIso = (utcIso: string, timeZone: string): string | null => {
  const ms = Date.parse(utcIso);
  if (Number.isNaN(ms)) return null;
  try {
    return formatInTimeZone(new Date(ms), timeZone, "yyyy-MM-dd'T'HH:mm:ssXXX");
  } catch {
    // An unknown IANA zone is a payload problem, and showing nothing beats showing a UTC wall clock.
    return null;
  }
};

/**
 * What to CALL the arrival inside a sentence — "…calculated when the gate estimate was 11:57 AM".
 *
 * A sibling of `arrivalLabel` rather than a reuse of it, because a label is not a noun: `arrivalLabel`
 * yields "At gate", which lower-cased into that sentence reads "when the at gate was 11:57 AM".
 *
 * Keyed on the same `LandingSource` as the label and sitting beside it, so the two vocabularies for one
 * arrival cannot drift apart. Keying it on the backend's snake_case column names instead would take a
 * camelCase source without complaint and return the default for every input — a silent wrong answer.
 *
 * Exhaustive, with no `default`: a new `LandingSource` member is a compile error here rather than a
 * silently wrong noun.
 *
 * The nullish case is the majority today — the V2 customer payload does not yet send the stored source —
 * and callers must pass the frozen source straight through rather than coalescing it to a live one. Not
 * knowing which kind of arrival the pickup was built on is the honest answer here; borrowing the kind of
 * a DIFFERENT arrival is how the noun ends up describing one time while the sentence quotes another.
 */
export const arrivalNoun = (source?: LandingSource | null): string => {
  if (!source) return "scheduled arrival";
  switch (source) {
    case "gateActual":
      return "gate arrival";
    case "gateEstimated":
      return "gate estimate";
    case "gateScheduled":
      return "scheduled gate arrival";
    case "actual":
      return "landing time";
    case "estimated":
      return "estimated arrival";
    case "scheduled":
      return "scheduled arrival";
  }
};

/** True pickup instant = landing + "delay driver arrival by" minutes. Use for validation vs now. */
export const pickupFromFlight = (landingIso: string, delayMinutes: number): Date =>
  new Date(Date.parse(landingIso) + delayMinutes * 60_000);

/** Date shifted so its UTC fields hold the wall-clock time at the offset embedded in `iso`. */
const asAirportWallClock = (iso: string): Date => {
  const match = iso.match(/([+-])(\d{2}):?(\d{2})$/);
  const offsetMs = match ? (match[1] === "-" ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10)) * 60_000 : 0;
  return new Date(Date.parse(iso) + offsetMs);
};

/**
 * The value to commit to `clientBooking.pickupDatetime` for a flight pickup.
 *
 * The booking payload renders pickupDatetime with a BROWSER-local `format(new Date(iso), …)` to the
 * naive "YYYY-MM-DD HH:mm:ss" the backend expects — which is correct only when the booker's browser
 * shares the pickup's timezone. A flight's landing time is AIRPORT-local, so someone booking a
 * Sydney arrival from a Manila browser would otherwise submit the Manila wall clock (2h off).
 *
 * To keep the pickup at the airport's wall clock regardless of where it's booked, we take the
 * airport-local wall clock of (landing + delay) and re-encode those calendar fields as a
 * browser-local Date. Its `toISOString()` is the instant that renders — via the payload's
 * browser-local format — back to the airport wall clock. This mirrors how the manual entry path
 * encodes the user's typed wall clock, so both paths carry the same "naive wall clock" semantics.
 */
export const flightPickupCommitValue = (landingIso: string, delayMinutes: number): Date => {
  const airportWall = new Date(asAirportWallClock(landingIso).getTime() + delayMinutes * 60_000);
  return new Date(
    airportWall.getUTCFullYear(),
    airportWall.getUTCMonth(),
    airportWall.getUTCDate(),
    airportWall.getUTCHours(),
    airportWall.getUTCMinutes(),
    0,
    0
  );
};

/**
 * The value to send as `flight_landing_used` — the landing the committed pickup was derived from, as an
 * instant carrying the ARRIVAL AIRPORT's own offset.
 *
 * The server verifies provenance with EXACT epoch equality (`BookingService::recordSuppliedBookingArrival`,
 * and `suppliedLandingProvenance` on the edit path): `landing + delay*60 !== pickup` refuses and logs.
 * Strict `!==` on integer timestamps — no tolerance window, no rounding. It reads the naive
 * `pickup_datetime` wall clock in the BOOKING LOCATION's timezone (`JobHydrator::prepareDateTimeObject`),
 * and `flightPickupCommitValue` builds that wall clock out of this same landing — so the pair agrees only
 * when this value is anchored to the same airport wall clock at the airport's own offset. Anything
 * anchored in UTC or browser-local misses by the zone difference.
 *
 * Three details are load-bearing, each of them a silent refusal on its own:
 *   - SECONDS ARE ZEROED, because `flightPickupCommitValue` drops them when it re-encodes the wall clock.
 *     A landing at :25:37 yields a pickup at :25:00 and misses the equality by 37 seconds.
 *   - NO FRACTIONAL SECONDS, and an explicit offset. `BookingResource::isParseableInstant` accepts only
 *     DATE_ATOM (`Y-m-d\TH:i:sP`) or a literal-`Z` UTC string, so `Date#toISOString()` — which emits
 *     `.000Z` — is rejected outright with a 400 on the authed path, and dropped in silence on the guest one.
 *   - THE AIRPORT'S OFFSET is preserved as given rather than normalised to `Z`. Both are the same instant
 *     to the server, but keeping the offset makes the value legible beside the wall clock it explains.
 *
 * Returns null for an unparseable landing rather than a guess. Omitting the field costs a booking its
 * provenance and the server resolves its own arrival; sending a WRONG one is refused on create and, on the
 * edit path, actively CLEARS a previously-good applied arrival.
 */
export const flightLandingUsedIso = (landingIso: string): string | null => {
  const match = landingIso.match(/(?:Z|([+-])(\d{2}):?(\d{2}))$/);
  if (!match || Number.isNaN(Date.parse(landingIso))) return null;
  // A `Z` landing is offset zero. Read off the string rather than the browser, which is the whole point.
  const offset = match[1] ? `${match[1]}${match[2]}:${match[3]}` : "+00:00";
  const wall = asAirportWallClock(landingIso);
  wall.setUTCSeconds(0, 0);
  const pad = (value: number) => String(value).padStart(2, "0");
  const date = `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}`;
  return `${date}T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}:00${offset}`;
};

/**
 * The day to ask Cirium about for an already-committed flight pickup.
 *
 * The flight date is an INPUT the client picks and the pickup is DERIVED from it, so reading the lookup
 * date off the pickup reads an input out of its own output. A 23:45 landing with a 30-minute wait commits
 * a pickup at 00:15 the next day; asking about that day finds no flight, the card claims it cannot find a
 * perfectly good one, and no landing is sent for precisely the late-arrival bookings the provenance is for.
 *
 * This IS `flight_pickup_derived_arrival` converted to the arrival airport's zone — the arrival the stored
 * pickup was built on, engine-applied where one exists, else the snapshot taken at booking. Not a
 * substitute for it: given what the draft carries, the same number. That field reaches the client in UTC,
 * and the arrival airport's zone is not available here — `ClientBookingDetail` carries no timezone, and
 * `toNaiveWallClockIso` consumes `pickup_time`'s offset before the panel sees it — so the only conversion
 * available is to recover the offset from the pair, and the arrival then cancels out of its own conversion:
 *
 *     offset       = pickupWall - (arrivalUtc + wait)
 *     arrivalLocal = arrivalUtc + offset = pickupWall - wait
 *
 * Which is this function. Subtracting the wait from the pickup's wall clock is that conversion in reduced
 * form, and it never reads a UTC wall clock as a local one — the mistake that would invert this bug for
 * early-morning arrivals, where 05:10 local on the 31st is 19:10 on the 30th in UTC.
 *
 * The committed pickup holds the AIRPORT's wall clock in its local fields (see `flightPickupCommitValue`),
 * so both the arithmetic and the formatting stay local.
 *
 * Exact whenever the stored arrival is the pickup's operand, which is what "the arrival the stored pickup
 * was derived from" means. Where the two have been pulled apart — a pickup clamped to MIN_LEAD_MINUTES or
 * hand-edited by an operator, the case `anchoredWorkingHolds` already tests for — the stored arrival is the
 * better answer and is unreachable, so this returns the pickup's own derivation instead. Never worse than
 * reading the pickup date directly, which is what it replaces.
 */
export const flightDateFromPickup = (pickupIso: string, delayMinutes: number | null): string | null => {
  const ms = Date.parse(pickupIso);
  if (Number.isNaN(ms)) return null;
  return formatDate(new Date(ms - (delayMinutes ?? 0) * 60_000), "yyyy-MM-dd");
};

/** "9:25 PM" — in the airport's own offset. */
export const formatFlightTime = (iso: string): string =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" }).format(asAirportWallClock(iso));

/** "30 Jul 2026" — in the airport's own offset. */
export const formatFlightDate = (iso: string): string =>
  new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(asAirportWallClock(iso));

/** Compact leg duration ("8h", "1h 30m", "45m"), or null when either end is unknown. */
export const flightDuration = (departureIso: string | null, arrivalIso: string | null): string | null => {
  if (!departureIso || !arrivalIso) return null;
  const minutes = Math.round((Date.parse(arrivalIso) - Date.parse(departureIso)) / 60_000);
  if (minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(" ");
};

/**
 * How far the flight has to move before the pickup re-derives, when the lookup carries no threshold of
 * its own.
 *
 * The real value is `materiality_threshold_minutes` on the flight payload — the engine's own number, 15
 * minutes domestic and 30 international. The frontend cannot derive that itself (the only signal on the
 * payload is airport country codes, and reading those would be reimplementing FlightClassifier), which is
 * why it has to come from the server: hardcoded at 30, the card holds a domestic pickup that the engine
 * has already moved.
 *
 * This is the FALLBACK for a lookup that omits the field. It stays at the wider 30 so the fallback
 * UNDER-reports rather than re-deriving over drift the engine has decided to ignore.
 *
 * Read in exactly one place (`flightUpdateIsMaterial`), so there is one rule about when it applies.
 */
export const FLIGHT_UPDATE_THRESHOLD_MINUTES = 30;

/** Whole minutes between two instants, sign-independent. Null when either is unparseable. */
export const driftMinutes = (fromIso: string, toIso: string): number | null => {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round(Math.abs(to - from) / 60_000);
};

/**
 * Whether the flight has moved far enough to re-derive the pickup from its fresh arrival.
 *
 * `thresholdMinutes` is the lookup's own `materiality_threshold_minutes` — the same number the ENGINE
 * applies to this flight: below it the engine leaves the booking alone, at or above it it re-anchors.
 *
 * A missing, zero or negative threshold falls back rather than being honoured. A payload saying "nothing
 * is material" and one saying "everything is" are both payload problems, and re-deriving the pickup on
 * every estimate wobble is the worse of the two failures.
 *
 * NOT applied to the client edit path — DEV-1531 removed its last two callers there, because the 15/30 rule
 * is a churn control for a poller and an edit is one deliberate human save the client sees disclosed before
 * confirming. It is kept because the rule itself is still live server-side and the mobile app carries its
 * own copy, so this is the one place the frontend states what that rule IS. It has no production caller in
 * THIS repo; do not read a call site's absence as the rule being gone.
 */
export const flightUpdateIsMaterial = (liveIso: string, storedIso: string, thresholdMinutes?: number | null): boolean => {
  const drift = driftMinutes(liveIso, storedIso);
  const threshold = thresholdMinutes && thresholdMinutes > 0 ? thresholdMinutes : FLIGHT_UPDATE_THRESHOLD_MINUTES;
  return drift !== null && drift >= threshold;
};

/**
 * Whether the flight will actually arrive as looked up, and so whether a pickup may be derived from it.
 *
 * Cancelled / diverted / incident flights (and `unknown` / `not_found`) still carry a scheduled time, but
 * pinning a pickup to it would be wrong — those fall through to manual entry.
 *
 * Shared rather than re-listed per surface: the date panel and the sheet-level re-derivation have to
 * agree about what counts as an arriving flight, or the same booking derives on one and not the other.
 */
export const flightWillArrive = (flight?: FlightDetails | null): boolean =>
  !!flight && (["scheduled", "en-route", "landed"] as FlightStatus[]).includes(flight.status);

/**
 * Whether the ENGINE has frozen this pickup against further flight movement, keyed on the reason it
 * recorded — `at_gate`, `cancelled`, `diverted`.
 *
 * A frozen pickup is one the engine will no longer move, so the client must not move it either: re-deriving
 * onto a fresh landing would put the booking ahead of the server with nothing to reconcile it back.
 *
 * Read only by the SHEET-LEVEL re-derivation, which is stricter than the date panel on purpose.
 * That panel gates on `flightDisruptionReason` — the two disruptions a client can act on — because it is
 * disclosing a status to a reader who is looking at the flight. This is a different question: whether a
 * save the client did not come here to make may rewrite their pickup. `at_gate` belongs in that set and not
 * in the other, and adding it there would change a behaviour DEV-1499 has under test.
 *
 * `no_gate_event` is deliberately absent — it is the ordinary path, not a freeze the client should be held
 * by. An unrecognised reason reads as NOT frozen rather than as a guess.
 */
export const isFlightPickupFrozen = (reason?: string | null): boolean =>
  reason === "at_gate" || reason === "cancelled" || reason === "diverted";
